<template>
  <div :class="['sofort fake-section', { large: screenParams.large }]">
    <div class="overlay" v-if="((sofortIdent && sofortIdent.verifying) || verifying) && !verified && !noMatch">
      <transition name="fade" appear>
        <section class="progress-screen">
          <spinner type="ring"/>
          <p>{{ i18n.t('base:verification_in_progress') }}</p>
        </section>
      </transition>
    </div>
    <div class="overlay" v-else-if="verified && !noMatch">
      <transition name="fade" appear>
        <section class="progress-screen">
          <span class="verified"></span>
          <p>{{ i18n.t('sofort:identity_verified') }}</p>
        </section>
      </transition>
    </div>
    <div class="overlay" v-else-if="noMatch">
      <transition name="fade" appear>
        <section class="progress-screen">
          <span class="no-match"></span>
          <p>{{ i18n.t('sofort:no_match') }}</p>
        </section>
      </transition>
    </div>
    <template>
      <div class="logo" />
      <h2 class="title">{{i18n.t('sofort:intro_header')}}</h2>
      <p class="description">{{i18n.t('sofort:intro_description')}}</p>
      <div class="spacer"/>
      <div class="footer">
        <p class="info">{{i18n.t('sofort:intro_info')}}</p>
       <button class="primary" @click="openIdent">
          <template v-if="loadingIdent">
             <spinner type="ring"/>
          </template>
          <template v-else>
            {{i18n.t('sofort:intro_button')}}
          </template>
        </button>
        <button class="link hidden" :class="{hidden: !ident.identURL || verifying}" @click="manualVerification">{{i18n.t('sofort:intro_manual')}}</button>
      </div>

      <transition name="slide-up">
        <div v-show="showMaunalVerificationPopup" class="verify-popup" @click="manualVerification">
          <div class="wrapper">
            <div class="title">{{ i18n.t('specifics:documents.upload') }}</div>
            <p class="hint">{{ i18n.t('specifics:documents.hint') }}</p>
            <a class="primary" @click.stop="manualVerificationFlow">
              {{i18n.t('sofort:intro_button')}}
            </a>
          </div>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Raven from 'raven-js'
import Mousetrap from 'mousetrap'

export default {
  props: ['screenParams'],

  data () {
    return {
      ident: {},
      verifying: false,
      error: null,
      verified: false,
      showMaunalVerificationPopup: false,
      noMatch: false,
      loadingIdent: false
    }
  },

  computed: mapState(['user', 'partner', 'dataStatus', 'sofortIdent', 'credentials', 'iovation', 'consent', 'sanction', 'app']),

  methods: {
    dateToString (date) {
      if (date && typeof date === 'string') {
        date = new Date(date)
      }
      try {
        let day = `0${date.getDate()}`.slice(-2)
        let month = `0${date.getMonth() + 1}`.slice(-2)
        let year = date.getFullYear()
        return `${year}-${month}-${day}`
      } catch (e) {
        return ''
      }
    },

    async fetchIdent (additionalData = {}) {
      try {
        const payload = Object.entries({
          partnerPublicKey: this.partner.publicKey,
          firstName: this.user.firstName,
          lastName: this.user.lastName,
          streetName: this.user.streetName && this.user.houseNumber && [this.user.streetName, this.user.houseNumber].join(' '),
          city: this.user.city,
          postalCode: this.user.postalCode,
          birthdate: this.user.birthdate && this.dateToString(this.user.birthdate),
          country: this.user.country,
          bankCode: this.user.bankCode || '',
          accountCountry: this.user.country,
          userVar0: this.partner.sofortRedirectURL,
          ...additionalData
        }).reduce((a, [k, v]) => ([null, undefined, ''].includes(v) ? a : {...a, [k]: v}), {})

        this.loadingIdent = true

        const response = await this.$sofortService.create(payload)
        this.ident = response

        this.loadingIdent = false
      } catch (e) {
        this.$rmt.trackEvent({
          category: 'sofort',
          action: 'fail'
        })

        this.$rmt.trackEvent({
          category: 'sofort',
          action: 'error:api_error'
        })
      }
    },

    async verifyIdent () {
      this.verifying = true

      this.$store.dispatch('updateSofortIdent', {
        verifying: true
      })
      try {
        let streetName, houseNumber
        let parsedStreet = this.sofortIdent.street.match(/(?:^|\s)(\d+\w?)(?!\w)/g)
        const isSofortValid = this.sofortIdent.agecheck_result === 'valid'

        if (parsedStreet && parsedStreet.length) {
          const houseNumberParsed = parsedStreet.join('')
          houseNumber = houseNumberParsed.trim()
          streetName = this.sofortIdent.street.replace(houseNumberParsed, '').trim()
        } else {
          streetName = this.sofortIdent.street
        }

        const sofortUserData = {
          firstName: this.sofortIdent.firstname,
          lastName: this.sofortIdent.lastname,
          streetName: streetName,
          houseNumber: houseNumber || this.user.houseNumber,
          city: this.sofortIdent.city,
          postalCode: this.sofortIdent.zipcode,
          birthdate: new Date(this.sofortIdent.birthday),
          country: this.sofortIdent.address_country_id
        }

        if (isSofortValid) {
          await this.compareSofortData({
            ...sofortUserData
          })
        }

        await this.$store.dispatch('updateUser', {
          ...sofortUserData,
          fullName: `${this.sofortIdent.firstname} ${this.sofortIdent.lastname}`,
          bankCode: this.sofortIdent.bank_code,
          accountCountry: this.sofortIdent.account_country_id
        })

        if (isSofortValid) {
          let hashConfirmed = true

          await this.fetchIdent({
            result: this.sofortIdent.agecheck_result,
            hash: this.sofortIdent.agecheck_hash
          })

          if (this.sofortIdent.agecheck_hash !== this.ident.hash) {
            hashConfirmed = false

            Raven.captureBreadcrumb({
              message: 'Compared hashes',
              category: 'processing',
              data: {
                agecheck_hash: this.sofortIdent.agecheck_hash,
                ident_hash: this.ident.hash
              }
            })
            Raven.captureException('Sofort hash mismatch')
          }

          await this.$store.dispatch('updateSofortIdent', {
            hashConfirmed,
            jwtSignData: this.ident.jwtSignData
          })

          await this.$store.dispatch('updateSofortIdent', {
            verifying: false
          })

          if (this.partner.variant === 'sofort-standalone') {
            const error = await this.checkForCallbackErrors()
            if (error) {
              this.$rmt.trackEvent({ category: 'sofort', action: `error:wrong_callbacks` })
              return this.$store.dispatch('replace', `/error/${error}`)
            }
          }

          this.verified = true

          if (isSofortValid) {
            this.$rmt.trackEvent({ category: 'sofort', action: 'success'
            })
          }
        } else {
          this.noMatch = true

          const failedAttemps = this.user.sofortFailedAttemps || 0
          await this.$store.dispatch('updateUser', {
            sofortFailedAttemps: failedAttemps + 1
          })

          if (this.user.sofortFailedAttemps && this.user.sofortFailedAttemps >= 2) {
            this.$rmt.trackEvent({ category: 'sofort', action: 'error:multiple_failed_attemps' })
            setTimeout(() => {
              this.$store.dispatch('updateSofortIdent', {
                verifying: false
              })
            }, 300)
            return this.$store.dispatch('replace', {
              path: '/error/sofortIdentError',
              skipDelay: true
            })
          }

          const error = await this.checkForCallbackErrors('sofortIdentError')
          if (error) {
            this.$rmt.trackEvent({ category: 'sofort', action: 'error:wrong_callbacks' })
            this.$store.dispatch('updateSofortIdent', {
              verifying: false
            })
            return this.$store.dispatch('replace', `/error/${error}`)
          }
        }

        if (this.noMatch) {
          this.$rmt.trackEvent({ category: 'sofort', action: 'error:validation_failed' })
        }

        this.$store.dispatch('updateApp', {
          showVerificationRecovery: true
        })

        setTimeout(() => {
          this.$store.dispatch('next', this.screenParams.nextScreenRoute)
        }, this.noMatch ? 500 : 3000)

        setTimeout(() => {
          this.$store.dispatch('updateSofortIdent', {
            verifying: false
          })
        }, 300)
      } catch (err) {
        this.$store.dispatch('updateSofortIdent', {
          agecheck_result: 'invalid'
        })

        this.$rmt.trackEvent({ category: 'sofort', action: 'error:sofortIdentInvalid' })

        this.$store.dispatch('replace', {
          path: '/error/sofortIdentInvalid',
          skipDelay: true
        })
      }
    },

    compareSofortData (data) {
      const results = Object.keys(data).filter(key => {
        if (key === 'birthdate' && typeof data.birthdate === 'object' && typeof this.user[key] === 'object') {
          return this.user.birthdate && this.user.birthdate.getTime && this.user.birthdate.getTime() === data.birthdate.getTime()
        }
        return data[key] === this.user[key]
      })

      if (results.length < Object.keys(data).length) {
        this.$store.dispatch('updateApp', {
          sofortDataChanged: true
        })
      }
    },

    async checkForCallbackErrors (errorName) {
      return new Promise(resolve => {
        setTimeout(() => {
          const errors = this.dataStatus.errors
          if (errors.length) {
            if (errorName) {
              const er = errors.find(error => error.name === errorName)
              resolve(er.name)
            } else {
              const firstErrorName = errors[0].name
              resolve(firstErrorName)
            }
          } else resolve(null)
        }, 2000)
      })
    },

    openIdent () {
      if (this.loadingIdent) return

      this.loadingIdent = true

      this.$rmt.trackEvent({
        category: 'sofort',
        action: this.app.isSofortDoubleCheck ? 're-send' : 'send',
        forceDeposit: true
      })

      if (this.user.gender === '' || this.credentials.email === '') {
        const email = this.credentials.email
        Raven.captureException(new Error(`Pre Sofort ident error: ${email === '' ? 'Email' : 'Gender'} is empty!`))
      }

      setTimeout(() => {
        this.$store.dispatch('openSofortIdent', {
          deposit: {
            user: this.user,
            credentials: this.credentials,
            iovation: this.iovation,
            consent: this.consent,
            sanction: this.sanction
          },
          identURL: this.ident.identURL
        })
      })
    },

    tryAgain () {
      this.$store.dispatch('updateSofortIdent', {
        verifying: true
      })

      let additionalData = {}

      if (this.sofortIdent) {
        additionalData = {
          firstName: this.sofortIdent.firstname,
          lastName: this.sofortIdent.lastname,
          city: this.sofortIdent.city,
          streetName: this.sofortIdent.street,
          postalCode: this.sofortIdent.zipcode,
          birthdate: this.sofortIdent.birthdate,
          country: this.sofortIdent.address_country_id
        }
      }

      this.fetchIdent(additionalData).then(() => {
        this.openIdent()
        this.$store.dispatch('updateApp', {
          sofortTryAgain: false
        })
      })
    },

    manualVerificationFlow () {
      this.$store.dispatch('updateApp', {
        manualVerificationFlow: true
      })
      this.$store.dispatch('updateSofortIdent', {
        agecheck_result: 'invalid'
      })

      this.$store.dispatch('next', this.screenParams.nextScreenRoute)
    },

    manualVerification () {
      if (this.partner.variant === 'sofort-standalone') {
        this.showMaunalVerificationPopup = !this.showMaunalVerificationPopup
      } else {
        this.manualVerificationFlow()
      }
    },

    refreshIdent () {
      if (!this.sofortIdent) {
        return this.fetchIdent()
      } else {
        if (this.app.sofortTryAgain) {
          this.tryAgain()
        } else {
          this.verifyIdent()
        }
      }
    }
  },

  watch: {
    sofortIdent () {
      if (!this.app.deviceIsDesktop) {
        // this.refreshIdent()
      }
    }
  },

  mounted () {
    setTimeout(() => {
      this.refreshIdent()
    }, 100)

    const button = this.$el.querySelector('.primary')
    if (button) Mousetrap.bind('enter', () => { button.click() })
  },

  destroyed () {
    Mousetrap.unbind(['enter'])
  }
}
</script>

<style lang="scss" scoped>
  @import '../assets/mixins';

  .sofort {
    flex: 1;
    display: flex;
    flex-direction: column;
    position: absolute;
    right: 0;
    bottom: 0;
    top: 0;
    left: 0;
    align-items: center;
    justify-content: center;
    color: $bodyInfotextColor;
    text-align: center;
    margin: -30px 0;

    .overlay {
      position: fixed;
      width: 100vw;
      height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: rgba(#04040F, .4);
      animation: background-color .3 ease-in-out;
      z-index: 10;

      @include desktop {
        position: absolute;
        height: 100%;
        width: 350px;
        left: -30px;
      }

      @include desktop-big {
        width: 610px;
        left: -30px;
      }

      .progress-screen {
        box-shadow: 0 12px 24px 0 rgba(0,0,0,0.2);
      }
    }

    .logo {
      background: url('~static/bank-transition.png');
      background-repeat: no-repeat;
      background-position: center;
      background-size: contain;
      height: 133px;
      width: 203px;   
      margin: 8em auto 40px;

      @include desktop {
        margin: 2em auto 30px;
      }

      @include desktop-big {
        margin: 5em auto 40px;
      }
    }

    .title {
      color: #1A212C;
      font-size: 25px;
      font-weight: $heroHeaderInfoWeight;
    }

    .description {
      color: #798291;
      font-size: 1rem;
      font-weight: 300;
      margin: 20px 0;
      line-height: 22px;
      max-width: 270px;
    }

    .spacer {
      flex: 1;
      width: 100%;
    }

    .footer {
      width: 100%;
      max-width: 380px;

      .info {
        color: #798291;
        font-size: 12px;
        line-height: 13px;
        font-weight: 300;
        margin: 16px 0;
        position: relative;
        display: inline-block;

        @include desktop {
          margin: 20px 0;
        }

        &::before {
          content: '';
          background-image: url('~static/lock.svg');
          height: 14px;
          width: 11px;
          display: inline-block;
          margin-right: 10px;
          position: relative;
          top: 2px;
        }
      }

      .link {
        font-size: 14px;
        color: #798291;
        line-height: 22px;
        text-decoration: underline;
        font-weight: 300;
        margin-bottom: 10px;
      }
    }

    .verify-popup {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      height: 100%;
      width: 100%;
      background: rgba(0,0,0,0.25);
      overflow: hidden;

      @include desktop {
        position: absolute;
        left: -30px;
        width: calc(100% + 60px);
        height: 610px;
        top: -30px;
      }

      > .wrapper {
        // position: fixed;
        font-family: $mainFontFamily;
        background: white;
        padding: 30px 15px 60px;
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;

        .title {
          color: #1A212C;
          font-size: 25px;
          margin-bottom: 12px;
          margin-top: 10px;
        }

        .hint {
          color: #798291;
          font-size: 1rem;
          line-height: 22px;
          padding: 0 20px;
          margin: 0 auto;
          margin-bottom: 70px;
          font-weight: 300;
          max-width: 380px;
          text-align: center;
        }

        .primary {
          background: $buttonPrimaryBackgroundColor;
          color: #ffffff;
          font-weight: normal;
          font-size: 16px;
          padding: 16.5px 38px;
          border-radius: 30px;
          box-shadow: none;
          text-align: center;
          cursor: pointer;
        }
      }
    }

    button.primary {
      opacity: 1;
      transition: opacity .2s ease, transform .2s ease;
      width: 315px;
      height: 60px;
      font-size: 20px;
      border-radius: 30px;
      width: 100%;
      position: relative;

      &.hidden {
        transform: translate3d(0, calc(100% + 70px), 0);
        opacity: 0;
      }
    }

    .spinner {
      top: calc(100% - 100px);
    }

    .progress-screen {
      background: #fff;
      width: 200px;
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      border-radius: 16px;
      position: absolute;
      z-index: 5;

      @media all and (-ms-high-contrast:none)
      {
        *::-ms-backdrop, & {
          transform: translate(-50%, -50%);
        } /* IE11 */
      }

      p {
        font-size: 16px;
        line-height: 22px;
        text-align: center;
        max-width: 150px;
        font-weight: 300;
        color: #798291;
        transform: perspective(1px);
      }

      .verified,
      .no-match {
        margin-bottom: 30px;
        background-repeat: no-repeat;
        background-size: contain;
      }

      .verified {
        background-image: url('~static/checkmark-sofort.svg');
        width: 40px;
        height: 40px;
      }

      .no-match {
        background-image: url('~static/no-match.svg');
        height: 43px;
        width: 49px;
      }

      /deep/ .ring {
        position: static;
        left: inherit;
        top: inherit;
        display: block;
        width: 58px;
        height: 58px;
        margin-bottom: 30px;

        div {
          width: 42px;
          height: 42px;
          border-color: #0FCECC transparent transparent transparent;
        }
      }
    }

    @include desktop {
      &.large {
        margin: 0;

        .overlay {
          left: 0;
          right: 0;
          width: 100%;
        }

        .progress-screen {
          width: 100%;
          height: 100%;
          border-radius: 0;
        }
      }
    }

  }
</style>



// WEBPACK FOOTER //
// src/screens/SofortIdent.vue